import { AlertCircle, MoreHorizontal, Loader2, Plus, Trash2, RefreshCw, ExternalLink } from "lucide-react";
import { DemoHint } from "@/components/demo/DemoHint";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface RiskLoad {
  id: string;
  origin: string;
  destination: string;
  signal: string;
  badge: string;
  badgeTone: "red" | "amber" | "slate";
  actionType: "remove" | "edit" | "repost";
}

const BADGE_STYLES: Record<RiskLoad["badgeTone"], string> = {
  red:   "bg-[hsl(0_65%_90%)] text-[hsl(0_60%_35%)]",
  amber: "bg-[hsl(38_75%_82%)] text-[hsl(30_65%_28%)]",
  slate: "bg-[hsl(215_18%_83%)] text-[hsl(215_28%_33%)]",
};

function formatTimeAgo(hours: number): string {
  if (hours < 1) return "< 1h ago";
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AtRiskLoadsPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [riskLoads, setRiskLoads] = useState<RiskLoad[]>([]);
  const [totalLoads, setTotalLoads] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const expiredCount = riskLoads.filter(l => l.badge === "Expired").length;

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetchAtRisk = async () => {
      try {
        const now = new Date();

        const { data: loads } = await supabase
          .from('loads')
          .select(`
            id, load_number, origin_city, origin_state,
            destination_city, destination_state,
            pickup_date, rate_amount, total_miles, created_at,
            offers(id, created_at, status)
          `)
          .eq('created_by', user.id)
          .eq('status', 'posted')
          .order('pickup_date', { ascending: true });

        if (!loads) { setRiskLoads([]); setTotalLoads(0); return; }

        setTotalLoads(loads.length);

        const risks: RiskLoad[] = [];

        for (const load of loads) {
          const origin = `${load.origin_city || '?'}, ${load.origin_state || '?'}`;
          const dest = `${load.destination_city || '?'}, ${load.destination_state || '?'}`;
          const pickupDate = new Date(load.pickup_date);
          const hoursUntilPickup = (pickupDate.getTime() - now.getTime()) / (1000 * 60 * 60);
          const hoursSincePost = (now.getTime() - new Date(load.created_at || '').getTime()) / (1000 * 60 * 60);

          const activeOffers = (load.offers || []).filter(
            (o: any) => o.status === 'pending' || o.status === 'accepted_by_carrier' || o.status === 'countered'
          );
          const lastOfferTime = activeOffers.length > 0
            ? Math.max(...activeOffers.map((o: any) => new Date(o.created_at).getTime()))
            : 0;
          const hoursSinceLastOffer = lastOfferTime > 0
            ? (now.getTime() - lastOfferTime) / (1000 * 60 * 60)
            : Infinity;

          // Expiring soon (< 6 hours until pickup)
          if (hoursUntilPickup > 0 && hoursUntilPickup <= 6) {
            risks.push({
              id: load.id,
              origin, destination: dest,
              signal: `Expiring in ${Math.ceil(hoursUntilPickup)}h`,
              badge: "Expiring",
              badgeTone: "red",
              actionType: "edit",
            });
            continue;
          }

          // Already expired
          if (hoursUntilPickup < 0) {
            const hoursExpired = Math.abs(hoursUntilPickup);
            risks.push({
              id: load.id,
              origin, destination: dest,
              signal: `Expired ${formatTimeAgo(hoursExpired)}`,
              badge: "Expired",
              badgeTone: "red",
              actionType: "remove",
            });
            continue;
          }

          // No offers and posted > 12h ago
          if (activeOffers.length === 0 && hoursSincePost > 12) {
            risks.push({
              id: load.id,
              origin, destination: dest,
              signal: `No offers in ${Math.floor(hoursSincePost)}h`,
              badge: "Low Engagement",
              badgeTone: "amber",
              actionType: "repost",
            });
            continue;
          }

          // Stale — last offer was > 18h ago
          if (activeOffers.length > 0 && hoursSinceLastOffer > 18) {
            risks.push({
              id: load.id,
              origin, destination: dest,
              signal: `No new offers in ${Math.floor(hoursSinceLastOffer)}h`,
              badge: "Low Engagement",
              badgeTone: "amber",
              actionType: "repost",
            });
            continue;
          }
        }

        setRiskLoads(risks.slice(0, 10));
      } catch (err) {
        console.error('AtRiskLoadsPanel error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAtRisk();
  }, [user]);

  const handleRemove = async (e: React.MouseEvent, loadId: string) => {
    e.stopPropagation();
    const { error } = await supabase.from('loads').update({ status: 'cancelled' as any }).eq('id', loadId);
    if (!error) {
      toast({ title: "Load archived", description: "Removed from your board." });
      setRiskLoads(prev => prev.filter(l => l.id !== loadId));
    }
  };

  const handleRepost = async (e: React.MouseEvent, loadId: string) => {
    e.stopPropagation();
    const { error } = await supabase.from('loads').update({ updated_at: new Date().toISOString() }).eq('id', loadId);
    if (!error) {
      toast({ title: "Load reposted", description: "Visibility refreshed." });
      setRiskLoads(prev => prev.filter(l => l.id !== loadId));
    }
  };

  const handleClearAllExpired = async () => {
    const expiredIds = riskLoads.filter(l => l.badge === "Expired").map(l => l.id);
    if (expiredIds.length === 0) return;
    const { error } = await supabase.from('loads').update({ status: 'cancelled' as any }).in('id', expiredIds);
    if (!error) {
      toast({ title: `${expiredIds.length} expired loads archived` });
      setRiskLoads(prev => prev.filter(l => l.badge !== "Expired"));
    }
  };

  const handleRowClick = () => {
    navigate('/my-loads');
  };

  return (
    <div className="flex flex-col rounded-lg border bg-card overflow-hidden h-full">
      <div className="flex items-center justify-between border-b bg-muted/20 px-4 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-3 w-3 text-danger" />
          <DemoHint hintKey="section.atRiskLoads" side="bottom">
            <h4 className="text-[12px] font-bold text-foreground">At-Risk Loads</h4>
          </DemoHint>
          {riskLoads.length > 0 && (
            <span className="rounded-full bg-danger/10 text-danger px-1.5 py-0.5 text-[9px] font-bold">{riskLoads.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {expiredCount >= 2 && (
            <button
              onClick={handleClearAllExpired}
              className="text-[9px] font-semibold text-danger hover:underline"
            >
              Clear all expired
            </button>
          )}
          <button className="text-muted-foreground hover:text-foreground transition-colors">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="text-[10px]">Analyzing loads…</span>
          </div>
        ) : totalLoads === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
            <Plus className="h-4 w-4" />
            <span className="text-[10px]">No loads posted yet</span>
            <button
              onClick={() => navigate('/post-load')}
              className="text-[10px] font-semibold text-primary hover:underline"
            >
              Post loads to get started
            </button>
          </div>
        ) : riskLoads.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <span className="text-[10px]">No at-risk loads — all clear!</span>
          </div>
        ) : (
          riskLoads.map((load, i) => (
            <div
              key={load.id}
              onClick={handleRowClick}
              className={cn(
                "flex items-center gap-3 px-4 py-1.5 hover:bg-muted/20 transition-colors cursor-pointer",
                i < riskLoads.length - 1 && "border-b"
              )}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-foreground whitespace-nowrap truncate">
                  {load.origin} <span className="text-muted-foreground">→</span> {load.destination}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{load.signal}</p>
              </div>
              <span className={cn("flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold leading-none whitespace-nowrap", BADGE_STYLES[load.badgeTone])}>
                {load.badge}
              </span>
              {load.actionType === "remove" && (
                <button
                  onClick={(e) => handleRemove(e, load.id)}
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 h-[24px] text-[9px] font-semibold text-muted-foreground hover:text-danger hover:border-danger/30 transition-colors"
                >
                  <Trash2 className="h-2.5 w-2.5" /> Remove
                </button>
              )}
              {load.actionType === "edit" && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate('/my-loads'); }}
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 h-[24px] text-[9px] font-semibold text-foreground hover:bg-muted/80 transition-colors"
                >
                  <ExternalLink className="h-2.5 w-2.5" /> Edit
                </button>
              )}
              {load.actionType === "repost" && (
                <button
                  onClick={(e) => handleRepost(e, load.id)}
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 h-[24px] text-[9px] font-semibold text-foreground hover:bg-muted/80 transition-colors"
                >
                  <RefreshCw className="h-2.5 w-2.5" /> Repost
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
